import { isAwayOn } from '../pages/time/timeData'
import type { ApiDay, ApiLeave, ApiLogLine } from './time'

export interface HourShare {
  label: string
  hours: number
}

export interface DayTotals {
  loggedHours: number
  plannedHours: number
  hasLogged: boolean
  hasPlan: boolean
  loggedStatus: ApiDay['status'] | null
  byProject: HourShare[]
  byActivity: HourShare[]
  leaveType: string | null
  leaveHalfDay: boolean
}

export function lineHours(line: ApiLogLine): number {
  return (
    (Number(line.hours) || 0) +
    (Number(line.overtime_hours) || 0) +
    (Number(line.nighttime_hours) || 0) +
    (Number(line.over_night_hours) || 0) +
    (Number(line.transit_hours) || 0)
  )
}

function round(hours: number): number {
  return Math.round(hours * 100) / 100
}

function share(lines: ApiLogLine[], labelOf: (line: ApiLogLine) => string): HourShare[] {
  const sums = new Map<string, number>()
  for (const line of lines) {
    const label = labelOf(line)
    sums.set(label, (sums.get(label) ?? 0) + lineHours(line))
  }
  return [...sums.entries()]
    .map(([label, hours]) => ({ label, hours: round(hours) }))
    .filter((s) => s.hours > 0)
    .sort((a, b) => b.hours - a.hours || a.label.localeCompare(b.label))
}

export function dayTotals(
  days: ApiDay[],
  leave: ApiLeave[],
  personId: string,
  date: string,
): DayTotals {
  const mine = days.filter((d) => d.employee_id === personId && d.day === date)
  const actual = mine.find((d) => d.kind === 'Actual')
  const plan = mine.find((d) => d.kind === 'Plan')
  const lines = actual?.lines ?? []
  const away = leave.find((a) => a.employee_id === personId && isAwayOn(a, date))
  return {
    loggedHours: round(lines.reduce((sum, l) => sum + lineHours(l), 0)),
    plannedHours: round((plan?.lines ?? []).reduce((sum, l) => sum + lineHours(l), 0)),
    hasLogged: actual != null,
    hasPlan: plan != null,
    loggedStatus: actual?.status ?? null,
    byProject: share(lines, (l) => l.product_service_title || ''),
    byActivity: share(lines, (l) => l.activity || ''),
    leaveType: away?.leave_type ?? null,
    leaveHalfDay: away?.half_day ?? false,
  }
}

export function formatHours(hours: number): string {
  return `${round(hours)}h`
}
